/** Presupuestos agrupados por código: filtros móviles y orden por generación. */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { EntityListCard } from '@/components/EntityListCard';
import { MobileLayout } from '@/components/MobileLayout';
import { MobileButton } from '@/components/MobileButton';
import { MobileFilterSheet } from '@/components/MobileFilterSheet';
import { MobileIcon } from '@/components/MobileIcon';
import { MobileSelect } from '@/components/MobileSelect';
import { SearchInput } from '@/components/SearchInput';
import { CardListSkeleton } from '@/components/LoadingSkeleton';
import { EmptyState } from '@/components/EmptyState';
import { PullIndicator } from '@/components/PullIndicator';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { useMobileBudgetsQuery } from '@/hooks/useMobileQuotations';
import { useMobileAuth } from '@/state/MobileAuthContext';
import { useMonetaryPrecision } from '@/state/MonetaryPrecisionContext';
import type { MobileBudget } from '@/hooks/useMobileQuotations';

function formatDate(value: string | undefined): string {
  if (!value) return '—';
  const date = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Estados que dejan de ser vigentes: la vigente es la mayor vN fuera de ellos. */
const NON_CURRENT_STATUSES = new Set(['superseded', 'cancelled']);

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos los estados' },
  { value: 'draft', label: 'Borrador' },
  { value: 'commercial', label: 'Comercial' },
  { value: 'approved', label: 'Aprobado' },
  { value: 'rejected', label: 'Rechazado' },
  { value: 'cancelled', label: 'Cancelado' },
  { value: 'superseded', label: 'Reemplazado' },
];

export type BudgetGroup = {
  key: string;
  budgetCode: string;
  current: MobileBudget;
  versions: number;
};

function timestamp(budget: MobileBudget): number {
  const value = budget.created_at ?? budget.budget_date;
  if (!value) return 0;
  const date = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

export function groupBudgetsByCode(items: MobileBudget[]): BudgetGroup[] {
  const byKey = new Map<string, MobileBudget[]>();
  for (const item of items) {
    const key = (item.budget_code ?? '').trim().toUpperCase();
    const list = byKey.get(key);
    if (list) list.push(item);
    else byKey.set(key, [item]);
  }
  const groups: BudgetGroup[] = [];
  for (const [key, rows] of byKey) {
    const sorted = [...rows].sort((a, b) => b.version_number - a.version_number);
    const current = sorted.find((row) => !NON_CURRENT_STATUSES.has(row.status)) ?? sorted[0];
    if (!current) continue;
    groups.push({ key, budgetCode: current.budget_code, current, versions: sorted.length });
  }
  return groups.sort((a, b) => {
    const byDate = timestamp(b.current) - timestamp(a.current);
    return byDate !== 0 ? byDate : a.budgetCode.localeCompare(b.budgetCode);
  });
}

function sortNewest(items: MobileBudget[]): MobileBudget[] {
  return [...items].sort((a, b) => timestamp(b) - timestamp(a));
}

export function BudgetsPage() {
  const { user, hasPermission } = useMobileAuth();
  const precision = useMonetaryPrecision();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedShowAll, setAppliedShowAll] = useState(false);
  const [appliedStatus, setAppliedStatus] = useState('all');
  const [appliedShowHistory, setAppliedShowHistory] = useState(false);
  const [liveShowAll, setLiveShowAll] = useState(false);
  const [liveStatus, setLiveStatus] = useState('all');
  const [liveShowHistory, setLiveShowHistory] = useState(false);
  const budgetsQuery = useMobileBudgetsQuery({ status: appliedStatus });
  const { bindRef, pulling, refreshing } = usePullToRefresh({
    onRefresh: () => budgetsQuery.refetch(),
    disabled: user?.active_tenant_id == null || !hasPermission('budgets.view'),
  });

  const filteredItems = useMemo(() => {
    const items = budgetsQuery.data?.items ?? [];
    const mine = appliedShowAll
      ? items
      : items.filter((item) => item.created_by && item.created_by === user?.id);
    if (!search.trim()) return sortNewest(mine);
    const q = search.trim().toLowerCase();
    return sortNewest(
      mine.filter(
        (item) =>
          item.budget_code.toLowerCase().includes(q) ||
          (item.customer_requirement?.client?.legal_name ?? '').toLowerCase().includes(q) ||
          (item.customer_requirement?.code ?? '').toLowerCase().includes(q),
      ),
    );
  }, [budgetsQuery.data, search, appliedShowAll, user?.id]);

  const groups = useMemo(() => groupBudgetsByCode(filteredItems), [filteredItems]);
  const visibleCount = appliedShowHistory ? filteredItems.length : groups.length;
  const activeFilterCount =
    (appliedShowAll ? 1 : 0) +
    (appliedStatus !== 'all' ? 1 : 0) +
    (appliedShowHistory ? 1 : 0);
  const hasFilters = activeFilterCount > 0 || search.trim() !== '';

  function openFilters() {
    setLiveShowAll(appliedShowAll);
    setLiveStatus(appliedStatus);
    setLiveShowHistory(appliedShowHistory);
    setFilterOpen(true);
  }

  function applyFilters() {
    setAppliedShowAll(liveShowAll);
    setAppliedStatus(liveStatus);
    setAppliedShowHistory(liveShowHistory);
    setFilterOpen(false);
  }

  function resetFilters() {
    setLiveShowAll(false);
    setLiveStatus('all');
    setLiveShowHistory(false);
    setAppliedShowAll(false);
    setAppliedStatus('all');
    setAppliedShowHistory(false);
    setFilterOpen(false);
  }

  function budgetCard(item: MobileBudget, versions: number) {
    return (
      <EntityListCard
        key={item.id}
        eyebrow={`${item.budget_code} · v${item.version_number}`}
        title={item.customer_requirement?.client?.legal_name ?? 'Presupuesto'}
        status={item.status}
        tag={versions > 1 ? `${versions} versiones` : undefined}
        ariaLabel={`${item.budget_code} · v${item.version_number}`}
        metadata={[
          ...(item.customer_requirement?.code
            ? [{ icon: 'assignment' as const, text: `RQ ${item.customer_requirement.code}` }]
            : []),
          ...(item.total !== undefined
            ? [
                {
                  icon: 'payments' as const,
                  text: precision.format(
                    'budgets',
                    'sale_price',
                    item.total,
                    item.currency ?? 'USD',
                  ),
                },
              ]
            : []),
          ...(item.budget_date
            ? [{ icon: 'schedule' as const, text: formatDate(item.budget_date) }]
            : []),
        ]}
        onClick={() => navigate(`/m/budgets/${item.id}`)}
      />
    );
  }

  return (
    <MobileLayout
      title="Presupuestos"
      subtitle={`${visibleCount} de ${budgetsQuery.data?.total ?? 0} en total`}
    >
      <div ref={bindRef} className="space-y-4">
        <PullIndicator pulling={pulling} refreshing={refreshing} />

        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Buscar por código o cliente..."
            />
          </div>
          <MobileButton
            variant="secondary"
            size="md"
            onClick={openFilters}
            icon={<MobileIcon name="filter_list" className="h-5 w-5" />}
            className="shrink-0 px-4"
          >
            {activeFilterCount > 0 ? `Filtros (${activeFilterCount})` : 'Filtros'}
          </MobileButton>
        </div>

        {budgetsQuery.isPending && <CardListSkeleton count={4} />}

        {budgetsQuery.isError && (
          <EmptyState
            icon="error_outline"
            title="No se pudo cargar"
            description="Verifica tu conexión e intenta de nuevo."
            actionLabel="Reintentar"
            onAction={() => void budgetsQuery.refetch()}
          />
        )}

        {!budgetsQuery.isPending && !budgetsQuery.isError && visibleCount === 0 && (
          <EmptyState
            icon={hasFilters ? 'search_off' : 'calculate'}
            title={hasFilters ? 'Sin resultados' : 'Sin presupuestos'}
            description={
              hasFilters
                ? 'Ninguno coincide con los filtros.'
                : appliedShowAll
                  ? 'Aún no hay presupuestos.'
                  : 'Aún no tienes presupuestos asignados.'
            }
          />
        )}

        <div className="space-y-3">
          {!appliedShowHistory &&
            groups.map((group) => budgetCard(group.current, group.versions))}
          {appliedShowHistory &&
            filteredItems.map((item) =>
              budgetCard(item, 1),
            )}
        </div>
      </div>

      <MobileFilterSheet
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={applyFilters}
        onReset={resetFilters}
      >
        <div>
          <p className="mb-1.5 text-sm font-medium text-text-secondary">Autor</p>
          <div
            role="radiogroup"
            aria-label="Filtrar presupuestos por autor"
            className="flex items-center gap-1 rounded-full bg-bg-tertiary p-1"
          >
            {(
              [
                { value: false, label: 'Solo míos' },
                { value: true, label: 'Todos' },
              ] as const
            ).map((option) => (
              <button
                key={option.label}
                type="button"
                role="radio"
                aria-checked={liveShowAll === option.value}
                onClick={() => setLiveShowAll(option.value)}
                className={clsx(
                  'flex h-11 min-h-[44px] flex-1 items-center justify-center rounded-full text-sm font-semibold transition-all active:scale-[0.97]',
                  liveShowAll === option.value
                    ? 'bg-white text-text-primary shadow-card'
                    : 'text-text-secondary hover:text-text-primary',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <MobileSelect
          label="Estado"
          staticLabel
          value={liveStatus}
          options={STATUS_OPTIONS}
          onChange={(event) => setLiveStatus(event.target.value)}
        />

        <button
          type="button"
          role="switch"
          aria-checked={liveShowHistory}
          onClick={() => setLiveShowHistory((next) => !next)}
          className="flex min-h-[52px] w-full items-center gap-3 rounded-xl border border-border-light bg-white px-4 py-2 text-left"
        >
          <span
            aria-hidden="true"
            className={clsx(
              'relative h-7 w-12 shrink-0 rounded-full transition-colors',
              liveShowHistory ? 'bg-primary' : 'bg-border',
            )}
          >
            <span
              className={clsx(
                'absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all',
                liveShowHistory ? 'left-[22px]' : 'left-0.5',
              )}
            />
          </span>
          <span className="min-w-0 flex-1 text-sm font-semibold text-text-primary">
            Ver históricas
          </span>
        </button>
      </MobileFilterSheet>
    </MobileLayout>
  );
}
